import { useMemo } from "react";

import { Paragraph } from "components/Paragraph";
import { Spinner } from "components/Spinner";
import { Sync } from "components/Sync";
import { Column, Table } from "components/Table";
import { useAllBrands } from "hooks/useAllBrands";
import { useAllGroups } from "hooks/useAllGroups";
import { useAllLines } from "hooks/useAllLines";
import { useAuthentication } from "hooks/useAuthentication";
import { useLayout } from "hooks/useLayout";
import { Product } from "types/models";
import { BLUECAR_ROLES_IDS, isBluecarRole } from "utils/models/user";

const baseColumns: Column<Product>[] = [
  { id: "name", label: "Nombre" },
  { id: "brand_name", label: "Marca" },
  { id: "line_name", label: "Linea" },
  { id: "group_name", label: "Grupo" },
  {
    id: "oem_codes",
    label: "Códigos OEM",
    renderer: (product) => (
      <Paragraph disableSelect>{product.oem_codes?.join(", ")}</Paragraph>
    ),
  },
];

const activeColumn: Column<Product> = {
  id: "active",
  label: "Activo",
  renderer: (product) => (Boolean(product.active) ? "Sí" : "No"),
};

const allFilters = [
  {
    id: "search",
    label: "Nombre / Descripción / Código OEM",
    style: { width: { md: 320 } },
    type: "text",
  },
  {
    id: "brand_id",
    label: "Marca",
    type: "select",
    options: [],
    multiple: true,
  },
  {
    id: "line_id",
    label: "Linea",
    type: "select",
    options: [],
    multiple: true,
  },
  {
    id: "group_id",
    label: "Grupo",
    type: "select",
    options: [],
    multiple: true,
  },
  {
    id: "active",
    label: "Activo",
    roles: BLUECAR_ROLES_IDS,
    type: "select",
    options: [
      { id: "true", label: "Sí" },
      { id: "false", label: "No" },
    ],
    multiple: false,
  },
] as const;

export const BackofficeProducts = () => {
  const { BackofficeLayout } = useLayout({
    isPrivate: true,
    title: "Productos",
    roles: BLUECAR_ROLES_IDS,
  });
  const { user } = useAuthentication();
  const { data: lines } = useAllLines();
  const { data: brands } = useAllBrands({});
  const { data: groups } = useAllGroups();

  const columns = useMemo(() => {
    if (user && isBluecarRole(user)) {
      return [...baseColumns, activeColumn];
    }
    return baseColumns;
  }, [user]);

  const filters = useMemo(() => {
    return allFilters.map((filter) => {
      if (
        filter.id === "line_id" ||
        filter.id === "brand_id" ||
        filter.id === "group_id"
      ) {
        const isLineFilter = filter.id === "line_id";
        const isBrandFilter = filter.id === "brand_id";
        const elements = isLineFilter ? lines : isBrandFilter ? brands : groups;
        return {
          ...filter,
          options:
            elements?.map((element) => ({
              id: element.id,
              label: element.name,
            })) || [],
        };
      }
      return filter;
    });
  }, [lines, brands, groups]);

  const anyFilterLoading = useMemo(() => {
    return filters.some(
      (filter) => filter.type === "select" && !filter.options.length
    );
  }, [filters]);

  return (
    <BackofficeLayout>
      {anyFilterLoading ? (
        <Spinner />
      ) : (
        <Table
          columns={columns}
          filters={filters}
          filtersRightNode={<Sync kind="products" />}
          queryKey="products"
          path="products"
        />
      )}
    </BackofficeLayout>
  );
};
